import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import VenueLivePage from './VenueLivePage';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Live Screen ✦ entitlement gate.
 *
 * Iter 170: Live Screen ✦ became universal infrastructure on every
 * paid edition (Keepsake / Room / Archive) per the user's decision —
 * "it's no longer a feature, it's ambient participation infrastructure".
 *
 * The gate now reads the canonical `features` list (which already
 * folds in the editions, the legacy `live_screen_purchased` flag,
 * Experience/Legacy/Premium tiers, and the universal-features
 * helper). One source of truth, no client-side guessing.
 *
 * Locked → renders the editorial paywall (walnut/ivory/gold) directing
 * the host to the editions page.
 * Unlocked → delegates to <VenueLivePage>, the cinematic projector.
 */
const INK = '#0c0805';
const INK_2 = '#1a120a';
const IVORY = '#f5efdf';
const GOLD = '#c19a4b';
const HAIRLINE = 'rgba(245,239,223,0.18)';
const FONT_DISPLAY = "'Cormorant Garamond', 'Playfair Display', Georgia, serif";
const FONT_BODY = "'Inter', system-ui, sans-serif";

const LivePage = () => {
  const { slug } = useParams();
  const [unlocked, setUnlocked] = useState(null);

  useEffect(() => {
    axios.get(`${API}/events/slug/${slug}`).then((r) => {
      const ev = r.data || {};
      const feats = Array.isArray(ev.features) ? ev.features : [];
      // Canonical: live_screen in the features list. Back-compat
      // fallbacks (flag / package_type) are already folded into the
      // server-side computation, so this single check is sufficient.
      const ok = feats.includes('live_screen') || !!ev.live_screen_purchased;
      setUnlocked(ok);
    }).catch(() => setUnlocked(false));
  }, [slug]);

  if (unlocked === null) {
    return (
      <div
        data-testid="live-loading"
        style={{
          position: 'fixed', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: `radial-gradient(ellipse at 50% 35%, ${INK_2} 0%, ${INK} 70%)`,
          color: 'rgba(245,239,223,0.5)', fontFamily: FONT_DISPLAY,
          fontStyle: 'italic', fontSize: 22, letterSpacing: '0.02em',
        }}
      >Listening to the room…</div>
    );
  }

  if (!unlocked) {
    return (
      <div
        data-testid="live-page"
        style={{
          position: 'fixed', inset: 0, overflow: 'hidden',
          background: `radial-gradient(ellipse at 50% 35%, ${INK_2} 0%, ${INK} 75%)`,
          color: IVORY, fontFamily: FONT_BODY,
        }}
      >
        <div
          data-testid="live-paywall"
          style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 28,
          }}
        >
          <div style={{ maxWidth: 520, width: '100%', textAlign: 'center' }}>
            <p style={{
              margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.32em',
              textTransform: 'uppercase', color: GOLD,
            }}>✦ The big screen</p>
            <h1 style={{
              margin: '20px 0 18px', fontFamily: FONT_DISPLAY,
              fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1.05,
              color: IVORY, letterSpacing: '-0.01em',
            }}>Live Screen ✦</h1>
            <p style={{
              margin: '0 auto 28px', maxWidth: 420,
              color: 'rgba(245,239,223,0.74)', fontSize: 15.5, lineHeight: 1.65,
              fontFamily: FONT_DISPLAY, fontStyle: 'italic',
            }}>
              The room's heartbeat, projected. Feelings, memories and arrivals
              quietly compose themselves into a cinematic atmosphere for the venue.
            </p>
            <div style={{
              padding: '22px 24px',
              border: `1px solid ${HAIRLINE}`,
              borderRadius: 6,
              marginBottom: 26,
              background: 'rgba(245,239,223,0.03)',
            }}>
              <p style={{
                margin: 0, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.28em',
                textTransform: 'uppercase', color: GOLD,
              }}>Included in</p>
              <p style={{
                margin: '10px 0 0', fontFamily: FONT_DISPLAY, fontSize: 22,
                color: IVORY, letterSpacing: '0.02em',
              }}>The Keepsake · The Room · The Archive</p>
            </div>
            <Link
              to={`/event/${slug}`}
              data-testid="live-paywall-cta"
              style={{
                display: 'inline-block', padding: '14px 28px',
                borderRadius: 999, background: `linear-gradient(180deg, ${GOLD}, #9a7836)`,
                color: '#1a0f04', fontWeight: 700, fontSize: 14,
                letterSpacing: '0.06em', textDecoration: 'none',
              }}
            >Return to the evening ✦</Link>
          </div>
        </div>
      </div>
    );
  }

  return <VenueLivePage />;
};

export default LivePage;
